import type { ImagePickerAsset } from 'expo-image-picker';
import { Platform } from 'react-native';

type ClipboardDataLike = {
  files?: ArrayLike<File> | null;
  items?: ArrayLike<DataTransferItem> | null;
};

type PasteEventLike = {
  clipboardData?: ClipboardDataLike | null;
  nativeEvent?: {
    clipboardData?: ClipboardDataLike | null;
    preventDefault?: () => void;
  } | null;
  preventDefault?: () => void;
};

function clipboardDataFromPasteEvent(event: unknown) {
  if (!event || typeof event !== 'object') {
    return null;
  }

  const pasteEvent = event as PasteEventLike;

  return pasteEvent.clipboardData ?? pasteEvent.nativeEvent?.clipboardData ?? null;
}

function imageFileFromClipboardData(data: ClipboardDataLike) {
  const files = Array.from(data.files ?? []);
  const file = files.find((candidate) => candidate.type.startsWith('image/'));

  if (file) {
    return file;
  }

  const items = Array.from(data.items ?? []);
  const item = items.find((candidate) => candidate.type.startsWith('image/'));

  return item?.getAsFile() ?? null;
}

function pastedImageFileName(file: File) {
  if (file.name?.trim()) {
    return file.name.trim();
  }

  const extension = file.type.split('/')[1]?.replace(/[^a-zA-Z0-9]/g, '');

  return `pasted-image-${Date.now()}.${extension || 'png'}`;
}

async function imageDimensions(uri: string) {
  if (typeof window === 'undefined' || typeof window.Image !== 'function') {
    return { height: 0, width: 0 };
  }

  return new Promise<{ height: number; width: number }>((resolve) => {
    const image = new window.Image();

    image.onload = () => {
      resolve({
        height: image.naturalHeight || image.height || 0,
        width: image.naturalWidth || image.width || 0,
      });
    };
    image.onerror = () => resolve({ height: 0, width: 0 });
    image.src = uri;
  });
}

export async function imageAssetFromPasteEvent(
  event: unknown,
): Promise<ImagePickerAsset | null> {
  if (Platform.OS !== 'web' || typeof URL === 'undefined') {
    return null;
  }

  const clipboardData = clipboardDataFromPasteEvent(event);

  if (!clipboardData) {
    return null;
  }

  const file = imageFileFromClipboardData(clipboardData);

  if (!file) {
    return null;
  }

  preventDefaultPaste(event);

  const uri = URL.createObjectURL(file);
  const dimensions = await imageDimensions(uri);

  return {
    file,
    fileName: pastedImageFileName(file),
    fileSize: file.size,
    height: dimensions.height,
    mimeType: file.type || 'image/png',
    type: 'image',
    uri,
    width: dimensions.width,
  };
}

export function preventDefaultPaste(event: unknown) {
  if (!event || typeof event !== 'object') {
    return;
  }

  const pasteEvent = event as PasteEventLike;

  pasteEvent.preventDefault?.();
  pasteEvent.nativeEvent?.preventDefault?.();
}
